import { LocalStorageService } from './storage';

const FAVORITES_KEY = 'webdevtoys_favorites';
const RECENT_TOOLS_KEY = 'webdevtoys_recent_tools';
const MAX_RECENT_TOOLS = 5;

export interface Tool {
  path: string;
  name: string;
  category: string;
}

export class ToolHistoryService {
  /**
   * Get the list of favorite tools
   */
  static getFavorites(): string[] {
    return LocalStorageService.getItem<string[]>(FAVORITES_KEY) || [];
  }

  /**
   * Add a tool to favorites
   */
  static addToFavorites(toolPath: string): void {
    const favorites = this.getFavorites();
    if (!favorites.includes(toolPath)) {
      favorites.push(toolPath);
      LocalStorageService.setItem(FAVORITES_KEY, favorites);
    }
  }

  /**
   * Remove a tool from favorites
   */
  static removeFromFavorites(toolPath: string): void {
    const favorites = this.getFavorites();
    const filtered = favorites.filter(path => path !== toolPath);
    LocalStorageService.setItem(FAVORITES_KEY, filtered);
  }

  /**
   * Check if a tool is in favorites
   */
  static isFavorite(toolPath: string): boolean {
    return this.getFavorites().includes(toolPath);
  }

  /**
   * Toggle favorite status
   */
  static toggleFavorite(toolPath: string): boolean {
    if (this.isFavorite(toolPath)) {
      this.removeFromFavorites(toolPath);
      return false;
    } else {
      this.addToFavorites(toolPath);
      return true;
    }
  }

  /**
   * Get recently accessed tools (paths only)
   */
  static getRecentTools(): string[] {
    return LocalStorageService.getItem<string[]>(RECENT_TOOLS_KEY) || [];
  }

  /**
   * Add a tool to recent tools list
   */
  static addToRecentTools(toolPath: string): void {
    if (toolPath === '/') return; // Don't track homepage

    let recentTools = this.getRecentTools();

    // Remove if already exists
    recentTools = recentTools.filter(path => path !== toolPath);

    // Add to the beginning
    recentTools.unshift(toolPath);

    // Keep only MAX_RECENT_TOOLS
    if (recentTools.length > MAX_RECENT_TOOLS) {
      recentTools = recentTools.slice(0, MAX_RECENT_TOOLS);
    }

    LocalStorageService.setItem(RECENT_TOOLS_KEY, recentTools);
  }

  /**
   * Clear all recent tools
   */
  static clearRecentTools(): void {
    LocalStorageService.removeItem(RECENT_TOOLS_KEY);
  }

  /**
   * Clear all favorites
   */
  static clearFavorites(): void {
    LocalStorageService.removeItem(FAVORITES_KEY);
  }
}
